import React, { useEffect, useState, useRef } from "react";
import {
  userList,
  getGroups,
  getMyLink,
  createMyLink,
  deleteLink,
  editLinks,
} from "../api/Links";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Loader from "../components/Loader";
import CustomPageHeader from "../components/pages/CustomPageHeader";

const MyLinks = () => {
  const location = useLocation();
  const selectedGroupId = location.state?.selectedGroupId;
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  const [invitePhoneNumber, setInvitePhoneNumber] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showSendMethodModal, setShowSendMethodModal] = useState(false);
  const [contactsImported, setContactsImported] = useState(false);

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestInProgress = useRef(false);
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  /** Fetch users with pagination */
  const fetchUsers = (pageNum = 1) => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    setLoading(true);
    setError("");

    userList(pageNum)
      .then((res) => {
        const newUsers = Array.isArray(res?.users) ? res.users : [];
        const apiLastPage = Number(res?.lastPage || 1);
        setUsers((prev) => {
          if (pageNum === 1) return newUsers;
          const ids = new Set(prev.map((u) => u.id));
          const filtered = newUsers.filter((u) => !ids.has(u.id));
          return [...prev, ...filtered];
        });

        setLastPage(apiLastPage);
      })
      .catch((err) => {
        setError(
          err.message || t("failed_to_fetch_users", "Failed to fetch users")
        );
        // Error toast is handled by centralized error handler
      })
      .finally(() => {
        setLoading(false);
        requestInProgress.current = false;
      });
  };
  useEffect(() => {
    if (selectedGroupId && groups.length > 0) {
      setSelectedGroup(selectedGroupId);
      // Optionally, fetch users for this group
      handleGroupClick(selectedGroupId);
    }
    // eslint-disable-next-line
  }, [selectedGroupId, groups]);
  useEffect(() => {
    if (!selectedGroup) {
      fetchUsers(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedGroup]);

  useEffect(() => {
    getGroups()
      .then((res) => {

        setGroups(res?.data || []);
      })
      .catch((err) => {
        // Error fetching groups
      });
  }, []); // Removed 't' dependency as it's a stable function from i18next

  useEffect(() => {
    if (!loaderRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();

    // Use a small delay to ensure DOM is ready
    const setupObserver = () => {
      const root = scrollContainerRef.current || null; // Fallback to viewport if container not available

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          const noMorePages = lastPage !== null && page >= lastPage;

          if (
            entry.isIntersecting &&
            !loading &&
            !requestInProgress.current &&
            !noMorePages
          ) {
            setPage((prev) => prev + 1);
          }
        },
        {
          root, // Use the scroll container as root, fallback to viewport
          rootMargin: root ? "50px" : "200px", // Smaller margin for container, larger for viewport
          threshold: 0,
        }
      );

      observerRef.current.observe(loaderRef.current);
    };

    // Small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timeoutId);
      observerRef.current?.disconnect();
    };
  }, [loading, lastPage, page]);

  // Backup scroll listener for mobile devices where intersection observer might fail
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

      scrollTimeoutRef.current = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
        const noMorePages = lastPage !== null && page >= lastPage;

        if (
          isNearBottom &&
          !loading &&
          !requestInProgress.current &&
          !noMorePages &&
          !selectedGroup // Only for user list, not group members
        ) {
          setPage((prev) => prev + 1);
        }
      }, 100);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [loading, lastPage, page, selectedGroup]);

  const handleGroupClick = async (groupId) => {
    if (!editMode) {
      setSelectedGroup(groupId);
      try {
        const res = await getMyLink(groupId);
        const groupArr = res?.data?.data || [];
        const group = groupArr.find((g) => g.id === groupId);
        const members = group?.members || [];
        setUsers(members);
        setSelectedUsers(members.map((member) => member.id));
        setPage(1);
        setLastPage(1);
      } catch {
        setUsers([]);
        setSelectedUsers([]);
      }
    }
  };

  const enterEditModeForSelectedGroup = async () => {
    if (!selectedGroup) {
      // ✅ No group selected → just enter "delete mode"
      setEditMode(true);
      setEditingGroupId(null);
      return;
    }

    const gid = selectedGroup;
    setEditingGroupId(gid);

    try {
      const res = await getMyLink(gid);
      const groupArr = res?.data?.data || [];
      const group = groupArr.find((g) => g.id === gid);

      setSelectedUsers(group?.members?.map((u) => u.id) || []);
      setSelectedGroup(null);
      setPage(1);
      setLastPage(null);
      fetchUsers(1);
      setEditMode(true);
    } catch (err) {
      // Error preparing edit mode
    }
  };

  const saveEditedGroup = async () => {
    if (!editingGroupId) {
      // ✅ No group selected → just exit edit mode
      setEditMode(false);
      return;
    }
    if (selectedUsers.length === 0) {
      toast.error(t("a_group_must_have_at_least_one_member"));
      return;
    }

    try {
      const group = groups.find((g) => g.id === editingGroupId);
      await editLinks({
        id: editingGroupId,
        name: group?.name || "",
        members: JSON.stringify(selectedUsers),
      });

      const refreshed = await getGroups();
      setGroups(refreshed?.data || []);

      const res2 = await getMyLink(editingGroupId);
      const groupArr2 = res2?.data?.data || [];
      const updatedGroup = groupArr2.find((g) => g.id === editingGroupId);

      setSelectedGroup(editingGroupId);
      setUsers(updatedGroup?.members || []);
      setPage(1);
      setLastPage(1);
    } catch (err) {
      toast.error(
        err.message || t("failed_to_update_group", "Failed to update group")
      );
    } finally {
      setEditMode(false);
      setEditingGroupId(null);
    }
  };

  const handleEditOrUpdateClick = () => {
    if (editMode) {
      saveEditedGroup();
    } else {
      enterEditModeForSelectedGroup();
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim())
      return toast.error(t("please_enter_a_group_name"));
    if (selectedUsers.length === 0) {
      toast.error(t("please_select_at_least_one_user"));
      return;
    }

    try {
      const res = await createMyLink({
        name: newGroupName.trim(),
        users: selectedUsers,
      });
      if (res?.success) {
        toast.success(t("group_created_successfully"));
        setShowGroupModal(false);
        setNewGroupName("");
        setSelectedUsers([]);

        const groupData = await getGroups();
        setGroups(groupData?.data || []);
      } else {
        toast.error(
          res?.message || t("failed_to_create_group", "Failed to create group")
        );
      }
    } catch (err) {
      toast.error(t("error_creating_group", "Error creating group"));
    }
  };

  // Silently import contacts from device
  const importContacts = async () => {
    if (contactsImported) return; // Don't import again if already done

    try {
      // Check if the Contacts API is supported
      if ("contacts" in navigator && "ContactsManager" in window) {
        const props = ["name", "tel"];
        const opts = { multiple: true };

        const contacts = await navigator.contacts.select(props, opts);
        const formattedContacts = contacts
          .map((contact) => ({
            name:
              contact.name && contact.name.length > 0
                ? contact.name[0]
                : "Unknown",
            phone: contact.tel && contact.tel.length > 0 ? contact.tel[0] : "",
          }))
          .filter((contact) => contact.phone);

        setContacts(formattedContacts);
        setContactsImported(true);
        setSelectedContacts(formattedContacts);
      } else {
        setContactsImported(true);
      }
    } catch (error) {
      setContactsImported(true);
    }
  };

  // Auto-import contacts and directly show send method selection
  const handleInviteModalOpen = async () => {
    // Import contacts silently in the background
    if (!contactsImported) {
      try {
        // Auto-import contacts without showing UI
        await importContacts();
      } catch (error) {
        // Contact import failed
      }
    }

    // Skip contact selection modal and go directly to send method selection
    setShowSendMethodModal(true);
  };

  // Validate phone number format
  const isValidPhoneNumber = (phone) => {
    if (!phone) return true; // Allow empty for optional fields
    const cleanPhone = phone.replace(/\D/g, "");
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  // Get all phone numbers from various sources
  const getAllPhoneNumbers = () => {
    const phones = [];

    // Add selected contacts
    selectedContacts.forEach((contact) => {
      if (contact.phone) phones.push(contact.phone);
    });

    // Add single phone input (backward compatibility)
    if (invitePhoneNumber && isValidPhoneNumber(invitePhoneNumber.trim())) {
      const cleanPhone = invitePhoneNumber.replace(/\D/g, "");
      phones.push(cleanPhone);
    }

    // Remove duplicates
    return [...new Set(phones)];
  };

  // Send invite via SMS or WhatsApp
  const sendInvite = (method) => {
    // Get all phone numbers using the enhanced function
    const phoneNumbers = getAllPhoneNumbers();

    if (phoneNumbers.length === 0) {
      toast.error(
        t(
          "please_enter_phone_number_or_select_contacts",
          "📞 Please enter a phone number or select contacts to send invitations"
        )
      );
      return;
    }

    // Simple default invitation message
    const messageToSend = t(
      "share_message",
      "Hey! Join me on VibeLinks - a great way to stay connected with friends and family. Download the app now!"
    );

    if (method === "sms") {
      if (phoneNumbers.length === 1) {
        // Single SMS
        const smsUrl = `sms:${phoneNumbers[0]}?body=${encodeURIComponent(
          messageToSend
        )}`;
        window.open(smsUrl, "_blank");
        // toast.success(
        //   t("sms_app_opened", "📱 SMS app opened. Please send the message.")
        // );
      } else {
        // Multiple SMS - send as group message
        const phoneNumbersString = phoneNumbers.join(",");
        const smsUrl = `sms:${phoneNumbersString}?body=${encodeURIComponent(
          messageToSend
        )}`;
        window.open(smsUrl, "_blank");
        // toast.success(
        //   t(
        //     "group_sms_opened",
        //     `📱 SMS app opened with ${phoneNumbers.length} contacts as group message. Please send the message.`
        //   )
        // );
      }
    } else if (method === "whatsapp") {
      if (phoneNumbers.length === 1) {
        // Single WhatsApp
        const whatsappUrl = `https://wa.me/${
          phoneNumbers[0]
        }?text=${encodeURIComponent(messageToSend)}`;
        window.open(whatsappUrl, "_blank");
        // toast.success(
        //   t("whatsapp_opened", "💬 WhatsApp opened. Please send the message.")
        // );
      } else {
        // Multiple WhatsApp - open WhatsApp for each number
        phoneNumbers.forEach((phoneNumber, index) => {
          setTimeout(() => {
            const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
              messageToSend
            )}`;
            window.open(whatsappUrl, "_blank");
          }, index * 1000); // 1 second delay between each WhatsApp to avoid overwhelming
        });
        // toast.success(
        //   t(
        //     "multiple_whatsapp_opened",
        //     `WhatsApp opened for ${phoneNumbers.length} contacts. Please send the messages.`
        //   )
        // );
      }
    }


    // Close modal after sending and reset state
    setShowSendMethodModal(false);
    setInvitePhoneNumber("");
    setSelectedContacts([]);
    setContactsImported(false); // Allow re-import next time
  };

  return (
    <>
      <CustomPageHeader
        pageContainerClass="overflow-hidden"
        pageContainerInnerClass="sm:p-0 md:p-0 h-[calc(100vh-80px)]"
        title={t("my_links", "Vibelinks")}
        // hideBack={true}
        // showPageHeader={false}
      >
        {/* Header */}
        <div className="flex items-center justify-end mb-4">
          {/* <button
          onClick={() => {
            setSelectedGroup(null);
            setEditMode(false);
            setEditingGroupId(null);
            setSelectedUsers([]);
            setPage(1);
            setLastPage(null);
            fetchUsers(1);
          }}
          className="text-lg  text-gray-800"
        >
          ←
        </button> */}
          {/* <h2 className="text-xl font-bold text-gray-800">My Links</h2> */}
          <button
            className="text-[#6d29f6] font-bold"
            onClick={handleEditOrUpdateClick}
          >
            {editMode ? t("update") : t("edit")}
          </button>
        </div>

        {/* Groups Tabs */}
        <div className="flex gap-5 overflow-x-auto pb-8">
          {groups.map((g) => (
            <div
              key={g.id}
              className="relative flex items-center text-gray-800"
            >
              <button
                onClick={async () => {
                  if (editMode && editingGroupId !== g.id) {
                    setEditMode(false);
                    setEditingGroupId(null);
                    setSelectedGroup(g.id);
                    try {
                      const res = await getMyLink(g.id);
                      const groupArr = res?.data?.data || [];
                      const group = groupArr.find((grp) => grp.id === g.id);
                      const members = group?.members || [];
                      setUsers(members);
                      setSelectedUsers(members.map((member) => member.id));
                      setPage(1);
                      setLastPage(1);
                    } catch {
                      setUsers([]);
                      setSelectedUsers([]);
                    }
                    return;
                  }
                  handleGroupClick(g.id);
                }}
                className={`px-4 py-2 w-max rounded-lg  border border-[#6d29f6] flex items-center gap-2 ${
                  selectedGroup === g.id ||
                  (editMode && editingGroupId === g.id)
                    ? "bg-[#6d29f6] text-white"
                    : "bg-white dark:bg-white  dark:text-black"
                }`}
              >
                {g.name}
              </button>
              {!editMode && (
                <span className="absolute -top-2 -right-2 mt-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow">
                  {g.count}
                </span>
              )}
              {editMode && !g.default && (
                <button
                  className="absolute -top-2 -right-2 mt-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow"
                  title="Delete Group"
                  onClick={() => {
                    setGroupToDelete(g.id);
                    setShowDeleteModal(true);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* User List with sticky bottom button */}
        <div
          ref={scrollContainerRef}
          className="lg:mb-5 space-y-4 relative overflow-y-auto h-[calc(100vh-300px)] sm:h-[calc(100vh-240px)] md:h-[calc(100vh-200px)]"
        >
          {error && <div className="text-red-500 text-center">{error}</div>}
          {users.map((user) => {
            const isSelected = selectedUsers.includes(user.id);

            return (
              <div
                key={user.id}
                className="user-card dark:bg-[#252739] flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={user.image || "/default-image.jpg"}
                    alt={user.full_name || "user"}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-lg dark:text-white">
                      {user.full_name}
                    </div>
                    <div className="text-sm text-[#6d29f6]">
                      {user.user_name}
                    </div>
                  </div>
                </div>
                <label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      if (isSelected) {
                        setSelectedUsers(
                          selectedUsers.filter((id) => id !== user.id)
                        );
                      } else {
                        setSelectedUsers([...selectedUsers, user.id]);
                      }
                    }}
                    className="peer sr-only"
                  />

                  <div
                    className={`h-5 w-5 border-2 rounded-md flex items-center justify-center 
                  transition-all duration-200 ${
                    isSelected
                      ? "bg-[#6d29f6] border-[#6d29f6]"
                      : "bg-transparent border-[#6d29f6]"
                  }`}
                  >
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="white"
                        className="w-3 h-3"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8.5 11.793l6.793-6.793a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </label>
              </div>
            );
          })}

          {loading && <Loader size={20} />}

          {/* Manual Load More button for mobile fallback */}
          {!selectedGroup && !loading && lastPage && page < lastPage && (
            <div className="flex justify-center py-4 sm:hidden">
              <button
                onClick={() => setPage((prev) => prev + 1)}
                className="bg-[#6d29f6] text-white px-6 py-2 rounded-lg text-sm font-medium"
              >
                {t("load_more", "Load More")}
              </button>
            </div>
          )}

          <div className="pb-20 sm:pb-16"></div>
          {/* {!hasMore && !loading && users.length > 0 && (
          <div className="text-center text-gray-400 text-sm">No more users</div>
        )} */}
          {/* <div ref={loaderRef} className="h-1" /> */}

          {/* Centered Bottom Buttons inside User List */}
          <div className="fixed sm:sticky w-full bottom-0 left-0 flex justify-center py-4 bg-white dark:bg-transparent dark:border-transparent sm:bg-transparent sm:border-0">
            <button
              onClick={() => setShowGroupModal(true)}
              className="bg-[#6d29f6] text-white px-6 py-3 rounded-lg shadow-lg pointer-events-auto"
              // disabled={editMode}
              style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.12)" }}
            >
              + {t("group")}
            </button>
            <button
              onClick={handleInviteModalOpen}
              className="bg-[#6d29f6] text-white ml-2 px-6 py-3 rounded-lg shadow-lg pointer-events-auto"
              style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.12)" }}
            >
              {t("invite_friends", "Invite Friend")}
            </button>
          </div>
        </div>

        {/* Create Group Modal */}
        {showGroupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
            <div className="bg-white dark:bg-dark-card ml-0 md:ml-[260px] rounded-2xl shadow-xl w-full max-w-xs p-6 flex flex-col items-center border border-white dark:border-dark-border">
              <label className="text-lg font-semibold mb-3 text-gray-500 dark:text-dark-text-secondary w-full text-left">
                {t("group_name", "Group Name")}
              </label>
              <input
                type="text"
                placeholder={t("enter_group_name", "Enter group name")}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="w-full p-3 bg-gray-100 dark:bg-dark-card-light border-none rounded-lg mb-6 text-gray-700 dark:text-dark-text text-base font-medium focus:outline-none"
              />
              <div className="flex gap-4 w-full">
                <button
                  onClick={() => setShowGroupModal(false)}
                  className="flex-1 py-3 bg-red-500 text-white rounded-lg font-bold text-lg"
                >
                  {t("cancel", "Cancel")}
                </button>
                <button
                  onClick={createGroup}
                  className="flex-1 py-3 bg-[#6d29f6] text-white rounded-lg font-bold text-lg"
                >
                  {t("save", "Save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Group Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="relative w-full max-w-xs">
              {/* Logo overlapping the modal */}
              <div className="absolute left-1/2 -top-8 -translate-x-1/2 z-10">
                <img
                  src="/logo-bg.png"
                  alt="VibeLinks"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                />
              </div>
              {/* Modal container */}
              <div className="bg-white dark:bg-[#252739] rounded-2xl shadow-xl p-6 pt-16 flex flex-col items-center">
                <h2 className="text-2xl font-bold mb-2 text-center text-black">
                  VibeLinks
                </h2>
                <p className="mb-6 text-gray-800 text-center text-base font-medium">
                  {t(
                    "delete_group_confirm",
                    "Are you sure, You want to delete this group?"
                  )}
                </p>
                {deleteError && (
                  <p className="text-red-500 mb-2">{deleteError}</p>
                )}
                <div className="flex gap-4 w-full justify-center">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setGroupToDelete(null);
                      setDeleteError("");
                    }}
                    className="flex-1 py-3 bg-red-500 text-white rounded-lg font-bold text-lg"
                    disabled={deleting}
                  >
                    {t("no", "No")}
                  </button>
                  <button
                    onClick={async () => {
                      const groupObj = groups.find(
                        (g) => g.id === groupToDelete
                      );
                      if (
                        groupObj &&
                        (groupObj.name === "Besties" ||
                          groupObj.name === "Family")
                      ) {
                        setDeleteError(
                          t(
                            "cannot_delete_default_groups",
                            "You cannot delete default groups (Besties, Family)."
                          )
                        );
                        return;
                      }
                      setDeleting(true);
                      setDeleteError("");
                      try {
                        await deleteLink(groupToDelete);
                        setGroups((prev) =>
                          prev.filter((g) => g.id !== groupToDelete)
                        );

                        if (selectedGroup === groupToDelete) {
                          setSelectedGroup(null);
                          setPage(1);
                          setLastPage(null);
                          fetchUsers(1);
                        }
                        if (editingGroupId === groupToDelete) {
                          setEditingGroupId(null);
                          setEditMode(false);
                          setSelectedGroup(null);
                          setSelectedUsers([]);
                          setPage(1);
                          setLastPage(null);
                          fetchUsers(1);
                        }

                        setShowDeleteModal(false);
                        setGroupToDelete(null);
                      } catch (err) {
                        setDeleteError(
                          err.message ||
                            t(
                              "failed_to_delete_group",
                              "Failed to delete group"
                            )
                        );
                      }
                      setDeleting(false);
                    }}
                    className="flex-1 py-3 bg-[#6d29f6] text-white rounded-lg font-bold text-lg"
                    disabled={deleting}
                  >
                    {t("yes", "Yes")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Method Selection Modal */}
        {showSendMethodModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
            <div className="bg-white dark:bg-dark-card ml-0 md:ml-[260px] rounded-2xl shadow-xl w-full max-w-xs p-6 flex flex-col items-center border border-white dark:border-dark-border">
              <div className="mb-4 text-center">
                <h3 className="text-xl font-bold text-gray-800 dark:text-dark-text mb-2">
                  {t("invite_friends", "Invite Friends")}
                </h3>
                {/* <p className="text-sm text-gray-600">
                  {selectedContacts.length > 0
                    ? t(
                        "contacts_selected",
                        `${selectedContacts.length} contacts selected`
                      )
                    : t("enter_phone_numbers", "Enter phone numbers to invite")}
                </p> */}
              </div>

              {/* Quick Manual Phone Input */}
              {selectedContacts.length === 0 && (
                <div className="w-full mb-6">
                  <label className="text-sm font-semibold mb-2 text-gray-600 dark:text-dark-text-secondary block">
                    {t("phone_numbers", "Phone Numbers")}
                  </label>
                  <input
                    type="tel"
                    placeholder={t("enter_phone_number", "Enter phone number")}
                    value={invitePhoneNumber}
                    onChange={(e) => setInvitePhoneNumber(e.target.value)}
                    className="w-full p-3 bg-gray-100 dark:bg-dark-card-light border-none rounded-lg mb-3 text-gray-700 dark:text-dark-text text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#6d29f6]"
                  />
                </div>
              )}

              {/* Method Selection Buttons */}
              <div className="flex flex-col gap-3 w-full">
                <button
                  onClick={() => sendInvite("sms")}
                  disabled={
                    selectedContacts.length === 0 && !invitePhoneNumber.trim()
                  }
                  className="w-full py-4 bg-[#007bff] text-white rounded-lg font-bold text-lg hover:bg-[#0056b3] transition-colors flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <span>📱</span>
                  {t("send_via_sms", "Send via SMS")}
                </button>

                <button
                  onClick={() => sendInvite("whatsapp")}
                  disabled={
                    selectedContacts.length === 0 && !invitePhoneNumber.trim()
                  }
                  className="w-full py-4 bg-[#25D366] text-white rounded-lg font-bold text-lg hover:bg-[#1da851] transition-colors flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <span>💬</span>
                  {t("send_via_whatsapp", "Send via WhatsApp")}
                </button>

                <button
                  onClick={() => {
                    setShowSendMethodModal(false);
                    // Reset state when canceling
                    setSelectedContacts([]);
                    setInvitePhoneNumber("");
                    setContactsImported(false); // Allow re-import next time
                  }}
                  className="w-full py-3 bg-gray-500 text-white rounded-lg font-bold text-lg hover:bg-gray-600 transition-colors"
                >
                  {t("cancel", "Cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
      </CustomPageHeader>
    </>
  );
};

export default MyLinks;
